/**
 * Página exibida quando o domínio é acessado sem um subdomínio válido.
 * Permite escolher entre os sistemas (EducaPaço / NTME) e redireciona
 * para o subdomínio correspondente.
 *
 * Esta tela é sempre renderizada no modo claro, independente do tema
 * da aplicação. Por isso usa elementos HTML simples (sem Card/Typography
 * do Ant Design), já que o CSS global aplica overrides com !important
 * nos componentes antd no modo escuro.
 */

import React, { useState } from "react";
import { RightOutlined } from "@ant-design/icons";

const PROD_BASE_DOMAIN = "pacodolumiar.ma.gov.br";

/**
 * Verifica se o acesso atual é em ambiente local
 */
const isLocalHost = hostname =>
  hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".localhost");

/**
 * Monta a URL do tenant preservando protocolo e porta em dev
 */
const buildTenantUrl = tenantSubdomain => {
  const { protocol, hostname, port } = window.location;

  if (isLocalHost(hostname)) {
    const portSuffix = port ? `:${port}` : "";
    return `${protocol}//${tenantSubdomain}.localhost${portSuffix}`;
  }

  return `https://${tenantSubdomain}.${PROD_BASE_DOMAIN}`;
};

const TENANTS = [
  {
    key: "educapaco",
    name: "EducaPaço",
    description: "Sistema de Gestão Educacional",
    logo: "/assets/logo-educapaco.png",
    color: "#021f57",
  },
  {
    key: "ntme",
    name: "NTME",
    description: "Núcleo Tecnológico Municipal de Educação",
    logo: "/assets/logo-ntme.png",
    color: "#1e40af",
  },
];

/**
 * Botão de seleção de tenant com a logo como elemento principal
 */
const TenantButton = ({ tenant }) => {
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    window.location.href = buildTenantUrl(tenant.key);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      aria-label={`Acessar ${tenant.name}`}
      style={{
        flex: "1 1 220px",
        maxWidth: "280px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        padding: "20px 16px",
        cursor: "pointer",
        background: "#ffffff",
        border: `2px solid ${hovered ? tenant.color : "#e5e7eb"}`,
        borderRadius: "12px",
        boxShadow: hovered ? "0 8px 24px rgba(0, 0, 0, 0.18)" : "0 2px 8px rgba(0, 0, 0, 0.08)",
        transform: hovered ? "translateY(-4px)" : "none",
        transition: "all 0.2s ease",
        outline: "none",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "96px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "12px 16px",
        }}
      >
        <img
          src={tenant.logo}
          alt={tenant.name}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      </div>

      <span
        style={{
          fontSize: "13px",
          textAlign: "center",
          color: "rgba(0, 0, 0, 0.45)",
        }}
      >
        {tenant.description}
      </span>

      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "14px",
          fontWeight: 600,
          color: hovered ? tenant.color : "rgba(0, 0, 0, 0.45)",
          transition: "color 0.2s ease",
        }}
      >
        Acessar <RightOutlined style={{ fontSize: "11px" }} />
      </span>
    </button>
  );
};

const InvalidSubdomain = () => {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isLocal = hostname ? isLocalHost(hostname) : false;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background com imagem e blur (mesmo visual da tela de login) */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: "url('/assets/bg_login.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: "blur(4px)",
          transform: "scale(1.1)",
        }}
      />
      {/* Overlay claro fixo */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(240, 242, 245, 0.75)",
        }}
      />

      {/* Card em modo claro fixo (div simples para escapar dos overrides globais do antd) */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "640px",
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.15)",
          padding: "32px 24px",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <h3
            style={{
              margin: "0 0 4px",
              fontSize: "24px",
              fontWeight: 600,
              color: "rgba(0, 0, 0, 0.88)",
            }}
          >
            Bem-vindo(a)!
          </h3>
          <span style={{ fontSize: "14px", color: "rgba(0, 0, 0, 0.45)" }}>
            Escolha o sistema que deseja acessar:
          </span>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "16px",
          }}
        >
          {TENANTS.map(tenant => (
            <TenantButton key={tenant.key} tenant={tenant} />
          ))}
        </div>

        {isLocal && (
          <div style={{ marginTop: "24px", textAlign: "center" }}>
            <span style={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.45)" }}>
              Ambiente local — você será redirecionado para{" "}
              <code>educapaco.pacodolumiar.ma.gov.br</code> ou{" "}
              <code>ntme.pacodolumiar.ma.gov.br</code>.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvalidSubdomain;
